import { useState, useEffect, useCallback } from "react";
import { getCurrentUser as getCurrentUserAdmin } from "../components/Admin/services/authService";
import { getCurrentUser as getCurrentUserGuest } from "../components/Guest/services/authService";
import { useCommitteeReview } from "./useCommitteeReview";

export const useCommitteeReviewLogic = (requestProgressId, persona) => {
  const { createReview, getReviews, isProcessing } = useCommitteeReview();
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [loadingReviews, setLoadingReviews] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = persona === "customer" ? await getCurrentUserGuest() : await getCurrentUserAdmin();
        setUser(userData);
      } catch (error) {
        console.error("Failed to fetch user", error);
      }
    };
    fetchUser();
  }, [persona]);

  const fetchReviews = useCallback(async () => {
    if (!user) return;
    
    setLoadingReviews(true);
    try {
      const reviewsData = await getReviews({
        requestProgressId,
        persona: persona
      });
      setData(reviewsData || []);
    } catch (error) {
      console.error("Failed to fetch reviews", error);
      setData([]);
    } finally {
      setLoadingReviews(false);
    }
  }, [user, requestProgressId, getReviews, persona]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleReview = useCallback(async (reviewType, rejectionReason = null) => {
    await createReview({
      requestProgressId,
      review: reviewType,
      rejectionReason,
      onSuccess: () => fetchReviews(),
    });
  }, [createReview, requestProgressId, fetchReviews]);

  const reviewStatus = data?.reviews?.find(
    (review) =>
      String(review.request_progress_id) === String(requestProgressId) &&
      String(review.member_id) === String(user?.id)
  )?.review;

  return {
    handleApprove: async () => await handleReview("approved"),
    handleDecline: async (rejectionReason) => await handleReview("rejected", rejectionReason),
    isProcessing: isProcessing || loadingReviews,
    isApproved: reviewStatus === "approved",
    isDeclined: reviewStatus === "rejected" || reviewStatus === "declined",
    loadingReviews,
    reviews: data?.reviews,
    allMembersFinished: data?.reviewStatus?.allMembersFinished
  };
};
